import { onScopeDispose, readonly, ref, type Ref } from 'vue';

/** Rendered body rows; group header rows carry no `data-row-id`. */
const BODY_ROW_SELECTOR = '.tbody > .tr[data-row-id]';

/**
 * Targets that keep their own mouse gesture: text inputs select text, buttons
 * and links click. Checkboxes do not — a drag across the row boxes marks rows.
 */
const NO_DRAG_START_SELECTOR = [
  'input',
  'textarea',
  'select',
  'button:not([role="checkbox"])',
  // A row link (`rowHref`) is the cell itself: a drag may start on it.
  'a[href]:not([data-nx-row-link])',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="combobox"]',
  '[role="switch"]',
  '[data-focus-group]',
].join(',');

/** Band inside the body's top and bottom edge that scrolls while the pointer is in it. */
const AUTO_SCROLL_EDGE_PX = 32;
/** Fastest auto-scroll step per animation frame. */
const AUTO_SCROLL_MAX_STEP_PX = 40;

type Gesture = {
  startRowId: string;
  /** `data-column-id` of the cell the drag started in, `null` outside a body cell. */
  startColumnId: string | null;
  /** Display index the live range currently ends at, -1 until it is applied. */
  endIdx: number;
  /** Ctrl/Cmd drag: the rows marked before, kept under the range. */
  base: Set<string> | undefined;
  /** Selection at the start, restored by Escape. */
  before: Set<string>;
  clientY: number;
  active: boolean;
  cancelled: boolean;
};

/**
 * Drag selection (`NxTable` `dragSelection`): press the mouse button on a body
 * row and drag over the others — every row from the start row to the one under
 * the pointer is marked, no Shift needed. Dragging back shrinks the range,
 * Ctrl/Cmd at the start adds the range to the rows marked before, Escape
 * restores the selection from before the drag.
 *
 * The gesture turns into a drag selection only once the pointer reaches
 * another row, so a plain click keeps its click behaviour. Near the top or
 * bottom edge of the body the table scrolls on its own; rows are looked up by
 * their rendered boxes, so the virtual list may swap rows in between.
 */
export function useDragSelection(params: {
  enabled: () => boolean;
  /** The table's scroll container. */
  scroller: Ref<HTMLElement | undefined>;
  /** The sticky header; the body viewport starts below it. */
  header: Ref<HTMLElement | undefined>;
  /** Display index of the loaded row with `rowId`, -1 when it is gone. */
  rowIndexOf: (rowId: string) => number;
  selectedIds: Ref<Set<string>>;
  /** Mark the rows between both display indices on top of `base`. */
  selectRange: (fromIdx: number, toIdx: number, base?: ReadonlySet<string>) => void;
  setSelectedIds: (ids: Iterable<string>) => void;
  /** A drag selection finished (not on Escape); `endIdx` is the row it ended on. */
  onEnd: (endIdx: number, startColumnId: string | null) => void;
}) {
  const dragSelecting = ref(false);
  let gesture: Gesture | null = null;
  let frame = 0;

  /** Visible part of the body: below the sticky header, above a horizontal scrollbar. */
  function bodyViewport(): { top: number; bottom: number } | null {
    const scroller = params.scroller.value;
    if (!scroller) return null;
    const box = scroller.getBoundingClientRect();
    const headerBottom = params.header.value?.getBoundingClientRect().bottom ?? box.top;
    const top = Math.max(box.top, headerBottom);
    return { top, bottom: Math.max(top, box.top + scroller.clientHeight) };
  }

  /**
   * Display index of the body row under the pointer's height, clamped into the
   * body viewport — a pointer above or below the table means the edge row. In
   * a gap (group header) or past the last row the nearest row counts.
   */
  function rowIndexAtPointer(clientY: number): number {
    const scroller = params.scroller.value;
    const viewport = bodyViewport();
    if (!scroller || !viewport) return -1;
    const y = Math.min(Math.max(clientY, viewport.top + 1), viewport.bottom - 1);
    let nearestId: string | null = null;
    let nearestDistance = Infinity;
    for (const row of scroller.querySelectorAll<HTMLElement>(BODY_ROW_SELECTOR)) {
      const box = row.getBoundingClientRect();
      if (box.height === 0) continue;
      const distance = y < box.top ? box.top - y : y >= box.bottom ? y - box.bottom + 1 : 0;
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestId = row.dataset.rowId ?? null;
        if (distance === 0) break;
      }
    }
    return nearestId === null ? -1 : params.rowIndexOf(nearestId);
  }

  function autoScroll(clientY: number): void {
    const scroller = params.scroller.value;
    const viewport = bodyViewport();
    if (!scroller || !viewport) return;
    const step = (depth: number) => Math.min(AUTO_SCROLL_MAX_STEP_PX, Math.ceil(depth / 2));
    if (clientY < viewport.top + AUTO_SCROLL_EDGE_PX) {
      scroller.scrollTop -= step(viewport.top + AUTO_SCROLL_EDGE_PX - clientY);
    } else if (clientY > viewport.bottom - AUTO_SCROLL_EDGE_PX) {
      scroller.scrollTop += step(clientY - (viewport.bottom - AUTO_SCROLL_EDGE_PX));
    }
  }

  /** Extend the live range to the row under the pointer. */
  function applyRange(): void {
    const g = gesture;
    if (!g) return;
    const startIdx = params.rowIndexOf(g.startRowId);
    if (startIdx === -1) {
      // The start row left the data (refetch): nothing to anchor the range on.
      end(false);
      return;
    }
    const endIdx = rowIndexAtPointer(g.clientY);
    if (endIdx === -1 || endIdx === g.endIdx) return;
    g.endIdx = endIdx;
    params.selectRange(startIdx, endIdx, g.base);
  }

  /** One frame of an active drag: scroll at the edges, then follow the rows. */
  function tick(): void {
    const g = gesture;
    if (!g?.active || g.cancelled) return;
    autoScroll(g.clientY);
    applyRange();
    frame = requestAnimationFrame(tick);
  }

  function activate(): void {
    const g = gesture;
    if (!g) return;
    g.active = true;
    dragSelecting.value = true;
    window.getSelection()?.removeAllRanges();
    applyRange();
    frame = requestAnimationFrame(tick);
  }

  function onMouseMove(event: MouseEvent): void {
    const g = gesture;
    if (!g || g.cancelled) return;
    // The button came up outside the window, where no mouseup reached us.
    if ((event.buttons & 1) === 0) {
      end(true);
      return;
    }
    g.clientY = event.clientY;
    if (g.active) {
      applyRange();
      return;
    }
    const idx = rowIndexAtPointer(event.clientY);
    if (idx !== -1 && idx !== params.rowIndexOf(g.startRowId)) activate();
  }

  function onMouseUp(event: MouseEvent): void {
    if (event.button === 0) end(true);
  }

  function onKeyDown(event: KeyboardEvent): void {
    const g = gesture;
    if (!g?.active || g.cancelled || event.key !== 'Escape') return;
    // The table's own Escape would clear the selection instead of restoring it.
    event.preventDefault();
    event.stopPropagation();
    g.cancelled = true;
    cancelAnimationFrame(frame);
    dragSelecting.value = false;
    params.setSelectedIds(g.before);
  }

  function onSelectStart(event: Event): void {
    event.preventDefault();
  }

  function onWindowBlur(): void {
    end(false);
  }

  /** The click that follows the mouseup of a drag must not collapse the range to one row. */
  function swallowNextClick(): void {
    const swallow = (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
    };
    window.addEventListener('click', swallow, { capture: true, once: true });
    // The click is dispatched in the same task as the mouseup, or not at all.
    setTimeout(() => window.removeEventListener('click', swallow, true), 0);
  }

  function end(fromPointer: boolean): void {
    const g = gesture;
    if (!g) return;
    gesture = null;
    cancelAnimationFrame(frame);
    window.removeEventListener('mousemove', onMouseMove, true);
    window.removeEventListener('mouseup', onMouseUp, true);
    window.removeEventListener('keydown', onKeyDown, true);
    window.removeEventListener('blur', onWindowBlur);
    document.removeEventListener('selectstart', onSelectStart, true);
    dragSelecting.value = false;
    if (!g.active) return; // a plain click: its click handler runs as always
    if (fromPointer) swallowNextClick();
    if (!g.cancelled && g.endIdx !== -1) params.onEnd(g.endIdx, g.startColumnId);
  }

  /**
   * Mouse button pressed on the body row `rowId`. The caller has already ruled
   * out rows that are not selectable (placeholders) and targets owned by
   * another drag (row reorder handle, native row drag).
   */
  function onRowMouseDown(event: MouseEvent, rowId: string): void {
    if (!params.enabled() || gesture || event.button !== 0) return;
    // Shift+click keeps its range-from-the-anchor meaning.
    if (event.shiftKey || event.altKey) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest(NO_DRAG_START_SELECTOR)) return;
    const additive = event.ctrlKey || event.metaKey;
    gesture = {
      startRowId: rowId,
      startColumnId:
        (target?.closest('[data-column-id]') as HTMLElement | null)?.dataset.columnId ?? null,
      endIdx: -1,
      base: additive ? new Set(params.selectedIds.value) : undefined,
      before: new Set(params.selectedIds.value),
      clientY: event.clientY,
      active: false,
      cancelled: false,
    };
    window.addEventListener('mousemove', onMouseMove, true);
    window.addEventListener('mouseup', onMouseUp, true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('blur', onWindowBlur);
    // Dragging over the cells would otherwise select their text.
    document.addEventListener('selectstart', onSelectStart, true);
  }

  /** A gesture is pending or running — a native row drag must not start meanwhile. */
  function isPending(): boolean {
    return gesture !== null;
  }

  onScopeDispose(() => end(false));

  return {
    dragSelecting: readonly(dragSelecting),
    onRowMouseDown,
    isPending,
  };
}
